import React, {
  useState,
  useMemo,
  useRef,
  useCallback,
  useEffect,
} from 'react';
import cx from 'classnames';
import { Form, FormGroup, Button } from 'reactstrap';
import ProductManagerCombobox from 'js/routes/UserGroups/components/UserGroup/ProductManagerList/ProductManagerCombobox';
import ProductManagerSearchResult from 'js/routes/UserGroups/components/UserGroup/ProductManagerList/ProductManagerSearchResult';
import styles from './ProductManagerForm.module.scss';

const ProductManagerForm = ({
  onAddProductManager,
  onCancel,
  onSearch,
  existingProductManagers = [],
}) => {
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const searchIdRef = useRef(0);
  const debounceTimeoutRef = useRef(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
        debounceTimeoutRef.current = null;
      }
    };
  }, []);

  const existingPMs = useMemo(
    () => existingProductManagers || [],
    [existingProductManagers]
  );

  const handleSearch = useCallback(
    async (query) => {
      if (!query) {
        setSearchResults([]);
        setSelectedUser(null);
        return;
      }

      const currentSearchId = ++searchIdRef.current;
      setIsSearching(true);

      try {
        const results = await onSearch(query);

        if (!isMountedRef.current) return;
        if (currentSearchId === searchIdRef.current) {
          console.log('results', results, existingPMs);
          setSearchResults(
            results.filter(
              (r) =>
                !existingPMs.some(
                  (pm) => pm.user_id === r.id && pm.archived_state === null
                )
            )
          );
        }
      } catch (error) {
        console.error('Search failed:', error);
        if (isMountedRef.current && currentSearchId === searchIdRef.current) {
          setSearchResults([]);
        }
      } finally {
        if (isMountedRef.current && currentSearchId === searchIdRef.current) {
          setIsSearching(false);
        }
      }
    },
    [onSearch, existingPMs]
  );

  const debouncedSearch = useCallback(
    (query) => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      debounceTimeoutRef.current = setTimeout(() => {
        handleSearch(query);
      }, 100);
    },
    [handleSearch]
  );

  const handleReset = useCallback(() => {
    setInputValue('');
    setSelectedUser(null);
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    searchIdRef.current++;
    onCancel();
  }, [onCancel]);

  const handleSelectUser = useCallback(
    async (selected) => {
      if (selected) {
        setSelectedUser(selected);
        setIsSubmitting(true);
        try {
          await onAddProductManager({ user_id: selected.id });
          if (!isMountedRef.current) return;
          handleReset();
        } finally {
          if (isMountedRef.current) setIsSubmitting(false);
        }
      }
    },
    [onAddProductManager, handleReset]
  );

  const handleInputChange = (value) => {
    setInputValue(value);
    debouncedSearch(value);
  };

  const handleAddUser = async () => {
    if (selectedUser) {
      setIsSubmitting(true);
      try {
        await onAddProductManager({ user_id: selectedUser.id });
        if (!isMountedRef.current) return;
        handleReset();
      } finally {
        if (isMountedRef.current) setIsSubmitting(false);
      }
    }
  };

  const isSelectedUserAlreadyAdded =
    selectedUser &&
    !isSubmitting &&
    existingPMs.some(
      (existing) =>
        (existing.user_id === selectedUser.id ||
          existing.user?.id === selectedUser.id) &&
        existing.archived_state === null
    );

  return (
    <Form className={styles['product-manager-form']}>
      <div className={styles['form-body']}>
        <div className={styles['product-manager-search']}>
          <FormGroup>
            <ProductManagerCombobox
              placeholder="Search for a user to add as product manager..."
              onChange={handleInputChange}
              onItemClick={handleSelectUser}
              value={inputValue}
              list={searchResults}
              itemToKey={(u) => u.id}
              itemToString={(u) => u.display_name || u.username || ''}
              itemComponent={ProductManagerSearchResult}
            />
            {!isSearching && inputValue && selectedUser && (
              <div
                className={cx(styles.feedback, {
                  'text-success': !isSelectedUserAlreadyAdded,
                  'text-danger': isSelectedUserAlreadyAdded,
                })}
              >
                {isSelectedUserAlreadyAdded
                  ? 'This user is already a product manager'
                  : 'User selected'}
              </div>
            )}
          </FormGroup>
        </div>

        <div className={styles['product-manager-actions']}>
          {selectedUser && !isSelectedUserAlreadyAdded ? (
            <Button
              color="primary"
              disabled={isSubmitting}
              onClick={handleAddUser}
            >
              Add Product Manager
            </Button>
          ) : null}
          <Button
            color="secondary"
            disabled={isSubmitting}
            onClick={handleReset}
          >
            Cancel
          </Button>
        </div>
      </div>
    </Form>
  );
};

export default ProductManagerForm;
